import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { fetchMsft, MSFT, CHAIN_ID, RPC, EXPLORER, fmtCompact } from 'data/msft';
import { AddressBar, Marquee, Mast, StatusBar, xpCss, webCss } from 'lib/xp';

// Network Neighborhood — onboarding for people who have never touched chain 4663.
// Three things a buyer needs: the network in their wallet, gas (ETH on 4663), and the
// pair asset (tokenized MSFT). Everything here is verifiable against the explorer; the
// "add network" button is EIP-3085. 2026-09-13: a 2001 "getting connected" page inside the
// XP frame (lib/xp `web` layer, navy variant).

const STEPS = [
  {
    n: 1,
    t: 'add Robinhood Chain to your wallet',
    d: `chain id ${CHAIN_ID} · currency ETH · rpc ${RPC.replace('https://', '')} · explorer ${EXPLORER.replace('https://', '')}`,
  },
  {
    n: 2,
    t: 'get gas',
    d: 'gas on 4663 is ETH. bridge a little ETH in through the Robinhood Chain bridge or an exchange withdrawal that supports the chain. you need cents, not dollars.',
  },
  {
    n: 3,
    t: 'get the pair asset: tokenized MSFT',
    d: '$INTERNET is quoted in MSFT, so you buy it WITH MSFT. swap USDG → MSFT on the deepest pool (button below). humans need minutes — do it before you need it.',
  },
  {
    n: 4,
    t: 'verify the CA',
    d: 'VERIFY.exe → the three places the real address appears. nothing else is real.',
  },
];

export default function Network() {
  const [d, setD] = useState(null);
  const [added, setAdded] = useState('');
  useEffect(() => {
    let alive = true;
    fetchMsft().then(x => alive && setD(x));
    return () => {
      alive = false;
    };
  }, []);

  async function addNetwork() {
    const eth = window.ethereum;
    if (!eth || !eth.request) {
      setAdded('no wallet found in this browser — add it manually with the values in step 1');
      return;
    }
    try {
      await eth.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: '0x' + CHAIN_ID.toString(16),
            chainName: 'Robinhood Chain',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: [RPC],
            blockExplorerUrls: [EXPLORER],
          },
        ],
      });
      setAdded('added ✓ — your wallet now knows the way');
    } catch (e) {
      setAdded('wallet declined — add it manually with the values in step 1');
    }
  }

  const tones = ['navy', 'felt', 'orange', 'purple'];

  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">View</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Network Neighborhood</span>
      </div>
      <AddressBar path="network.internetcoin.vip/setup.htm" />
      <div className="xp__client web web--navy">
        <Mast
          kicker="getting connected"
          title="NETWORK SETUP WIZARD"
          right={<span className="web__sticker web__sticker--orange">4 STEPS</span>}
        />
        <Marquee>
          ★ Robinhood Chain · chain id {CHAIN_ID} ★ gas is ETH ★ the pair asset is tokenized Microsoft (MSFT) ★ add the network, get gas, get MSFT, verify the CA ★ you need cents, not dollars ★
        </Marquee>

        <div className="web__body">
          <div className="web__note nn__note">
            <b>Note:</b> tokenized stocks on this chain are issued by a third party under their own eligibility rules — check them.
          </div>
          {STEPS.map((s, i) => (
            <div className={`web__panel web__panel--${tones[i]}`} key={s.n}>
              <div className="web__panel__t">Step {s.n} of {STEPS.length} — {s.t}</div>
              <div className="web__step">
                <span className="web__step__n">{s.n}</span>
                <div className="nn__d">
                  {s.d}
                  {s.n === 1 && (
                    <div className="web__row nn__act">
                      <button type="button" className="web__btn web__btn--blue nn__btn" onClick={addNetwork}>
                        Add network to wallet
                      </button>
                      {added && <span className="nn__added">{added}</span>}
                    </div>
                  )}
                  {s.n === 3 && (
                    <div className="web__row nn__act">
                      <a
                        className="web__btn nn__btn"
                        href={d && d.pairUrl ? d.pairUrl : `${EXPLORER}/token/${MSFT}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        deepest MSFT pool ↗
                      </a>
                      {d ? (
                        <span className="nn__added">
                          MSFT / {d.quote} · <span className="web__led">{fmtCompact(d.liq)}</span> liquidity right now
                        </span>
                      ) : (
                        <span className="nn__added">live pool data loads in MSFT.exe</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="web__foot">Best viewed in Internet Explorer 6 at 800×600 · © 2001 Internet Coin · no dial tone required</div>
        </div>
      </div>

      <StatusBar>
        <span>
          MSFT token:{' '}
          <a href={`${EXPLORER}/token/${MSFT}`} target="_blank" rel="noreferrer noopener">
            {MSFT.slice(0, 10)}… ↗
          </a>
        </span>
        <span>chain {CHAIN_ID}</span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

const Div = styled.div`
  ${xpCss};
  ${webCss};
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .xp__client {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 0;
  }
  .nn__note {
    margin: 0 0 12px;
  }
  .nn__d {
    line-height: 1.5;
    color: #222;
    word-break: break-word;
    flex: 1;
    min-width: 0;
  }
  .nn__act {
    margin-top: 8px;
  }
  .nn__btn {
    font-size: 12px;
    padding: 6px 14px;
  }
  .nn__added {
    color: #4a3d2b;
    font-size: 10.5px;
  }
  @media (max-width: 640px) {
    .web__mast__right {
      display: none;
    }
  }
`;
